import PageLayout from "../../components/PageLayout";
import { useResponsive } from "../../lib/useResponsive";

const benefits = [
  { icon: "📈", title: "Systematic Withdrawal", desc: "Monthly or bi-weekly payouts — grow your position and receive income simultaneously.", hi: true },
  { icon: "🏠", title: "Loan Support", desc: "Access liquidity using SSN holdings as collateral via Charles Schwab Pledge-Asset-Line based on Availability", hi: true },
  { icon: "🎉", title: "Community Events", bullets: ["Annual gatherings & DFW meetups", "Access to Mahismati Mahel — upcoming private resort at Estes Park, CO"] },
  { icon: "👥", title: "Network Access", desc: "Job referrals, HR coordination, and business networking across USA & India." },
  { icon: "⭐", title: "Merit Incentives", desc: "Entry discounts and referral rewards earned through real contribution." },
  { icon: "🌍", title: "Branch Leadership", desc: "Start or lead a local branch in your city or country." },
];

const joinSteps = [
  { num: "1", title: "Reach Out", p: "Connect with an SSN member or Branch Officer near you." },
  { num: "2", title: "Learn", p: "Attend a Zoom session or local branch meetup." },
  { num: "3", title: "Sign", p: "Review and sign the SSN Membership Agreement." },
  { num: "4", title: "Invest & Grow", p: "Wire funds, receive your units, watch value compound." },
];

export default function BenefitsPage() {
  const { isMobile, isTablet } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;

  return (
    <PageLayout
      eyebrow="Member Benefits & How to Join"
      title={<>Benefits that go <em>beyond returns</em>.</>}
      subtitle=""
    >
      {/* Benefits Grid */}
      <div style={{
        ...S.grid,
        gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "repeat(auto-fit, minmax(280px, 1fr))",
        gap: isMobile ? 12 : 16,
        marginBottom: isMobile ? 28 : 40,
      }}>
        {benefits.map((b, i) => (
          <div key={i} style={{ ...S.card, ...(b.hi ? S.bxHi : S.bxStd), padding: isMobile ? "16px" : "24px" }}>
            <div style={S.benefitHeader}>{b.icon} {b.title}</div>
            {b.desc && <p style={S.desc}>{b.desc}</p>}
            {b.bullets && (
              <ul style={S.bulletList}>
                {b.bullets.map((bl, j) => <li key={j} style={S.bulletItem}>{bl}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Fee Banner */}
      <div style={{
        ...S.feeBanner,
        padding: isMobile ? "16px" : 24,
        gap: isMobile ? 16 : 24,
        marginBottom: isMobile ? 28 : 40,
      }}>
        <div style={{ ...S.feeZero, fontSize: isMobile ? 36 : 44 }}>0%</div>
        <div style={S.feeText}>
          <h4 style={S.bannerTitle}>Management Fee — Forever Zero</h4>
          <p style={S.bannerDesc}>All operational costs under 1% flow back to community — events, gifts, branch development.</p>
        </div>
      </div>

      {/* Join Steps */}
      <div style={{
        ...S.joinSteps,
        gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "repeat(auto-fit, minmax(200px, 1fr))",
        gap: isMobile ? 16 : 20,
        marginBottom: isMobile ? 28 : 40,
      }}>
        {joinSteps.map((s) => (
          <div key={s.num} style={S.joinStep}>
            <div style={S.joinStepNum}>{s.num}</div>
            <div style={S.joinStepText}>
              <h5 style={S.stepTitle}>{s.title}</h5>
              <p style={S.stepDesc}>{s.p}</p>
            </div>
          </div>
        ))}
      </div>

      {/* YouTube Banner */}
      <a
        href="https://www.youtube.com/@shivsenatrust"
        target="_blank"
        rel="noopener noreferrer"
        style={{ ...S.ytBanner, padding: isMobile ? "14px 16px" : "16px 20px", marginBottom: isMobile ? 32 : 48 }}
      >
        <div style={S.ytIconBox}>
          <div style={S.ytPlayTriangle} />
        </div>
        <div style={S.ytText}>
          <h5 style={S.ytTitle}>ShivSena Networks on YouTube</h5>
          <p style={S.ytDesc}>Community talks, fund updates &amp; education — @shivsenatrust</p>
        </div>
        <div style={S.ytArrow}>↗</div>
      </a>

      <div style={{ ...S.tagline, fontSize: isMobile ? 13 : 15 }}>
        "Not just investors. A family that grows together — toward financial freedom." 🌿
      </div>
    </PageLayout>
  );
}

const S: Record<string, React.CSSProperties> = {
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 40 },
  card: { padding: "24px", borderRadius: 12, display: "flex", flexDirection: "column", gap: 8 },
  bxHi: { background: "rgba(29, 158, 117, 0.05)", border: "1px solid rgba(29, 158, 117, 0.2)" },
  bxStd: { background: "rgba(255, 255, 255, 0.03)", border: "1px solid rgba(255, 255, 255, 0.07)" },
  benefitHeader: { fontSize: 15, fontWeight: 600, color: "var(--text-primary)" },
  desc: { fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6, margin: 0 },
  bulletList: { margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 },
  bulletItem: { fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 },
  feeBanner: {
    display: "flex", gap: 24, padding: 24, background: "rgba(255, 255, 255, 0.02)",
    border: "1px solid rgba(255, 255, 255, 0.06)", borderRadius: 12, marginBottom: 40, alignItems: "center",
    flexWrap: "wrap",
  },
  feeZero: { fontSize: 44, fontWeight: 800, color: "var(--teal-light)", lineHeight: 1, flexShrink: 0 },
  feeText: { flex: 1, minWidth: 200 },
  bannerTitle: { fontSize: 16, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 },
  bannerDesc: { fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5, margin: 0 },
  joinSteps: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20, marginBottom: 40 },
  joinStep: { display: "flex", gap: 14, alignItems: "flex-start" },
  joinStepNum: {
    width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.15)", color: "var(--teal-light)",
    fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  joinStepText: { flex: 1 },
  stepTitle: { fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 },
  stepDesc: { fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5, margin: 0 },
  ytBanner: {
    display: "flex", gap: 16, padding: "16px 20px", background: "rgba(255, 0, 0, 0.03)",
    border: "1px solid rgba(255, 0, 0, 0.12)", borderRadius: 12, marginBottom: 48,
    alignItems: "center", textDecoration: "none", transition: "background 0.2s ease",
  },
  ytIconBox: {
    width: 40, height: 40, borderRadius: 8, background: "#ff0000",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  ytPlayTriangle: {
    width: 0, height: 0, borderStyle: "solid",
    borderWidth: "6px 0 6px 10px", borderColor: "transparent transparent transparent #ffffff",
    marginLeft: 2,
  },
  ytText: { flex: 1, minWidth: 0 },
  ytTitle: { fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 2 },
  ytDesc: { fontSize: 13, color: "var(--text-secondary)", margin: 0 },
  ytArrow: { fontSize: 16, color: "var(--text-muted)", paddingRight: 4, flexShrink: 0 },
  tagline: { textAlign: "center", fontSize: 15, fontStyle: "italic", color: "var(--text-muted)", marginTop: 16 },
};
